import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Share2, Link2, Facebook, Mail, Check } from "lucide-react";

const XIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
);

export const episodeUrl = (episode) => `${process.env.REACT_APP_BACKEND_URL}/api/share/episodes/${episode.id}`;

export const ShareButton = ({ episode, size = "md", className = "", testId = "share-button" }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, up: false });
  const ref = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const url = episodeUrl(episode);
  const text = `${episode.title}${episode.show?.name ? ` — ${episode.show.name}` : ""}`;

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!ref.current?.contains(e.target) && !menuRef.current?.contains(e.target)) setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", close);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { document.removeEventListener("mousedown", close); window.removeEventListener("scroll", onScroll); };
  }, [open]);

  const openMenu = () => {
    const r = btnRef.current.getBoundingClientRect();
    const up = window.innerHeight - r.bottom < 220;
    setPos({ top: up ? r.top - 8 : r.bottom + 8, left: Math.min(r.left, window.innerWidth - 240), up });
    setOpen(true);
  };

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
      else {
        const ta = document.createElement("textarea"); ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0"; document.body.appendChild(ta); ta.select();
        const ok = document.execCommand("copy"); document.body.removeChild(ta);
        if (!ok) throw new Error("copy failed");
      }
      setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1500);
    } catch { window.prompt("Copy this link:", url); }
  };

  const onClick = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
      try { await navigator.share({ title: episode.title, text, url }); return; } catch { /* fall through to menu */ }
    }
    if (open) setOpen(false); else openMenu();
  };

  const enc = encodeURIComponent;
  const targets = [
    ["X / Twitter", XIcon, `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`],
    ["Facebook", Facebook, `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`],
    ["Email", Mail, `mailto:?subject=${enc(text)}&body=${enc(`${text}\n\n${url}`)}`],
  ];
  const dims = size === "lg" ? "h-14 px-5 gap-2 text-sm" : "h-9 px-3 gap-1.5 text-xs";

  return (
    <div className="relative inline-block" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button ref={btnRef} onClick={onClick} className={`${dims} inline-flex items-center rounded-full border border-white/15 bg-white/5 font-semibold text-slate-200 hover:bg-white/10 hover:text-white transition-colors ${className}`} aria-label="Share episode" data-testid={testId}>
        <Share2 size={size === "lg" ? 18 : 14} /> Share
      </button>
      {open && createPortal(
        <div ref={menuRef} className="fixed z-[80] w-56 rounded-xl border border-white/10 bg-[#0F1C33] shadow-2xl p-1.5 on-dark" style={{ top: pos.up ? undefined : pos.top, bottom: pos.up ? window.innerHeight - pos.top : undefined, left: pos.left }} data-testid={`${testId}-menu`}>
          <button onClick={copy} className="w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-white/10 hover:text-white" data-testid={`${testId}-copy`}>{copied ? <Check size={15} className="text-emerald-400" /> : <Link2 size={15} />} Copy link</button>
          {targets.map(([label, Icon, href]) => (
            <a key={label} href={href} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-white/10 hover:text-white" data-testid={`${testId}-${label.split(" ")[0].toLowerCase()}`}><Icon size={15} /> {label}</a>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};
